package dev.mobilearchitecture.web

import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionProperty
import com.varabyte.kobweb.compose.css.boxShadow
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.jetbrains.compose.web.css.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.ui.styleModifier

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    // This site does not need scrolling itself, but this is a good demonstration for how you might enable this in your
    // own site. Note that we only enable smooth scrolling unless the user has requested reduced motion, which is
    // considered a best practice.
    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(
                "Inter", "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Oxygen", "Ubuntu",
                "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "sans-serif"
            )
            .fontSize(18.px)
            .lineHeight(1.6)
    }

    ctx.stylesheet.registerStyleBase("h1, h2, h3, h4, h5, h6") {
        Modifier.fontWeight(FontWeight.Bold)
    }

    // Silk dividers only extend 90% by default; we want full width dividers in our site
    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }
}

val HeadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(3.cssRem)
        .textAlign(TextAlign.Start)
        .lineHeight(1.2) //1.5x doesn't look as good on very large text
}

val SubheadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .textAlign(TextAlign.Start)
        .color(colorMode.toPalette().color.toRgb().copyf(alpha = 0.8f))
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}

val PrimaryButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .padding(topBottom = 1.25.cssRem, leftRight = 2.5.cssRem)
            .borderRadius(9999.px) // pill shape
            .fontSize(1.2.cssRem)
            .fontWeight(FontWeight.Bolder)
            .lineHeight(1.0)
            .boxShadow(offsetX = 0.px, offsetY = 4.px, blurRadius = 10.px, spreadRadius = 2.px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(79, 70, 229, 0.3f))
            .transition(Transition.of("transform", 250.ms), Transition.of("box-shadow", 250.ms), Transition.of("background-image", 250.ms))
            .styleModifier {
                property("background-image", "linear-gradient(to right, #4f46e5, #6366f1)")
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
            .color(Colors.White)
    }
    cssRule(":hover") {
        Modifier
            .scale(1.08)
            .boxShadow(offsetX = 0.px, offsetY = 12.px, blurRadius = 20.px, spreadRadius = 4.px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(79, 70, 229, 0.5f))
            .styleModifier {
                property("background-image", "linear-gradient(to right, #6366f1, #818cf8)")
            }
    }
    cssRule(":active") {
        Modifier.scale(0.95)
    }
}
